/**
 * A folha impressa: o que sai **fora da mancha** e como o capítulo abre.
 *
 * Até aqui o documento era só texto: margens, corpo e recuos vinham da norma, e
 * o resto era papel em branco. Um livro e uma revista impressos não são assim —
 * eles têm **cabeçalho corrido** (o *running head*: o autor de um lado, o título
 * do outro), **número de página** no pé ou no alto, filete separando a faixa do
 * texto, cada capítulo abrindo em folha nova, e às vezes uma letra capitulada
 * puxando o primeiro parágrafo.
 *
 * Duas coisas moram aqui:
 *
 * 1. **As escolhas** (`FolhaDoDocumento`, `FormatoDeCapitulo`) — o que a pessoa
 *    marcou no editor. Tudo opcional: documento salvo antes deste recurso não tem
 *    nenhum desses campos e continua saindo como sempre saiu (Regra 04).
 * 2. **A decisão de onde cada coisa cai** (`molduraDaFolha`, `rotuloDoCapitulo`),
 *    em caixas nomeadas — esquerda, centro e direita do alto e do pé. Quem escreve
 *    o CSS é o site, e ele não decide nada: se a regra mudar, muda aqui e os
 *    testes acompanham (a mesma divisão de trabalho do resto do motor).
 *
 * **O que o navegador não faz** (e por isso não está aqui, para não prometer o
 * que não sai no papel): cabeçalho corrido com o **título do capítulo daquela
 * página** (isso exigiria `string-set`, que o Chromium não implementa), imagem
 * dentro da faixa (`content: url()` não desenha), capítulo forçado a cair sempre
 * na página da direita (`break-before: right` é ignorado) e recomeço da contagem
 * de páginas (`counter-reset: page` também). O número sai correto e alterna de
 * lado; o resto, não.
 */
/* ------------------------------------------------------------------ */
/* Cabeçalho e rodapé                                                  */
/* ------------------------------------------------------------------ */
/**
 * Como o cabeçalho corrido é escrito.
 *
 * - `TITULO` — o título do documento ao centro. É o cabeçalho de livro mais
 *   comum: discreto, em corpo menor, em cima de uma linha de texto.
 * - `AUTOR_E_TITULO` — autor à esquerda, título à direita, a **dupla espelhada**
 *   das revistas e das coletâneas (é a convenção do cabeçalho de página dupla,
 *   aqui repetida em todas as folhas porque o navegador não troca o texto entre
 *   páginas pares e ímpares).
 * - `LIVRE` — o que a pessoa escrever (o nome da coleção, da editora, da
 *   disciplina).
 */
export const ESTILOS_DE_CABECALHO = ['NENHUM', 'TITULO', 'AUTOR_E_TITULO', 'LIVRE'];
export const ROTULO_ESTILO_DE_CABECALHO = {
    NENHUM: 'Sem cabeçalho',
    TITULO: 'Título do documento, ao centro',
    AUTOR_E_TITULO: 'Autor à esquerda, título à direita',
    LIVRE: 'Texto que eu escrever',
};
export const DESCRICAO_ESTILO_DE_CABECALHO = {
    NENHUM: 'A folha sai limpa em cima — o padrão de quem só quer o número.',
    TITULO: 'O título (e o subtítulo, quando houver) em corpo pequeno, ao centro do alto da folha.',
    AUTOR_E_TITULO: 'A dupla espelhada dos livros e revistas: quem escreveu de um lado, o título do outro.',
    LIVRE: 'Um texto seu na faixa: o nome da coleção, da editora, da disciplina.',
};
/** Como o rodapé é escrito (o pé da folha, abaixo da mancha). */
export const ESTILOS_DE_RODAPE = ['NENHUM', 'TITULO', 'LIVRE'];
export const ROTULO_ESTILO_DE_RODAPE = {
    NENHUM: 'Sem rodapé',
    TITULO: 'Título do documento, ao centro',
    LIVRE: 'Texto que eu escrever',
};
export const DESCRICAO_ESTILO_DE_RODAPE = {
    NENHUM: 'Só o número da página, quando ele estiver no pé.',
    TITULO: 'O título do documento em corpo pequeno, ao centro do pé da folha.',
    LIVRE: 'Um texto seu no pé da folha (o nome da editora, o endereço, o ano).',
};
/**
 * Onde o número da página entra.
 *
 * `TOPO_DIREITA` é a ABNT: canto superior direito, a partir da primeira folha do
 * texto (a contagem corre desde a capa, mas só o texto mostra o número).
 * `PE_EXTERNO` é o livro: número no canto de fora do pé — à direita nas folhas
 * ímpares, à esquerda nas pares —, que é onde o polegar cai quando a pessoa
 * folheia.
 */
export const POSICOES_DO_NUMERO = [
    'NENHUMA',
    'TOPO_DIREITA',
    'TOPO_CENTRO',
    'PE_CENTRO',
    'PE_DIREITA',
    'PE_EXTERNO',
];
export const ROTULO_POSICAO_DO_NUMERO = {
    NENHUMA: 'Sem número',
    TOPO_DIREITA: 'Alto, à direita (ABNT)',
    TOPO_CENTRO: 'Alto, ao centro',
    PE_CENTRO: 'Pé, ao centro',
    PE_DIREITA: 'Pé, à direita',
    PE_EXTERNO: 'Pé, no lado de fora (livro)',
};
export const DESCRICAO_POSICAO_DO_NUMERO = {
    NENHUMA: 'Nenhuma folha recebe número.',
    TOPO_DIREITA: 'O canto superior direito, como a norma dos trabalhos acadêmicos pede.',
    TOPO_CENTRO: 'Ao centro do alto da folha, acima do texto.',
    PE_CENTRO: 'Ao centro do pé, como em romance e livro de bolso.',
    PE_DIREITA: 'No canto inferior direito de todas as folhas.',
    PE_EXTERNO: 'Alterna com a folha: à direita nas ímpares, à esquerda nas pares.',
};
/** De que letra a faixa é feita. */
export const FONTES_DA_FAIXA = ['DOCUMENTO', 'SERIFADA', 'SEM_SERIFA'];
export const ROTULO_FONTE_DA_FAIXA = {
    DOCUMENTO: 'A mesma letra do documento',
    SERIFADA: 'Com serifa (Times, Georgia)',
    SEM_SERIFA: 'Sem serifa (Arial, Helvetica)',
};
/** Onde o texto próprio da faixa fica na largura da folha. */
export const POSICOES_DA_FAIXA = ['ESQUERDA', 'CENTRO', 'DIREITA'];
export const ROTULO_POSICAO_DA_FAIXA = {
    ESQUERDA: 'À esquerda',
    CENTRO: 'Ao centro',
    DIREITA: 'À direita',
};
/** O estilo de faixa como ele nasce: discreto, do jeito que livro impresso faz. */
export function estiloDaFaixaPadrao() {
    return {
        negrito: false,
        italico: false,
        sublinhado: false,
        versalete: false,
        caixaAlta: false,
        cor: '#333333',
        espacamento: 0,
        fonte: 'DOCUMENTO',
    };
}
/**
 * O estilo da faixa do documento, com o padrão onde o campo está ausente.
 *
 * A cor e o espaçamento são conferidos aqui: eles vão parar dentro de uma folha
 * de estilo, e um valor solto no meio do CSS derrubaria as regras seguintes (já
 * aconteceu com aspas num texto). O que não for hexadecimal de seis dígitos volta
 * para o cinza padrão.
 */
export function estiloDaFaixa(documento) {
    const escolhido = { ...estiloDaFaixaPadrao(), ...(documento.folha?.estiloDaFaixa ?? {}) };
    return {
        ...escolhido,
        cor: /^#[0-9a-f]{6}$/i.test(escolhido.cor ?? '') ? escolhido.cor.toLowerCase() : '#333333',
        espacamento: Math.min(40, Math.max(0, Number.isFinite(escolhido.espacamento) ? escolhido.espacamento : 0)),
    };
}
/**
 * A folha padrão de cada modelo.
 *
 * O **trabalho acadêmico e o artigo** seguem a norma: número no canto superior
 * direito, sem faixa nenhuma — a NBR 14724 não pede cabeçalho corrido, e
 * inventalo num TCC seria criar regra que a banca não espera. O **livro** adota
 * o que o livro impresso faz: número no pé, no lado de fora.
 *
 * As seções primárias começam em folha nova no trabalho e no livro (é o que a
 * pessoa espera de "capítulo"), e **não** no artigo: artigo científico é texto
 * corrido, e cada seção abrindo em folha nova deixaria buracos brancos no meio
 * do trabalho — quem quiser marca a seção, uma a uma.
 */
export function folhaPadrao(modelo) {
    return {
        cabecalho: 'NENHUM',
        rodape: 'NENHUM',
        numeracao: modelo === 'LIVRO' ? 'PE_EXTERNO' : 'TOPO_DIREITA',
        filete: false,
        corpoPt: 8,
        capituloEmFolhaNova: modelo !== 'ARTIGO_CIENTIFICO',
    };
}
/** A folha do documento, com o padrão do modelo onde o campo está ausente. */
export function folhaDoDocumento(documento) {
    return { ...folhaPadrao(documento.modelo), ...(documento.folha ?? {}) };
}
/**
 * A formatação no padrão do modelo — o que o botão **Padrão ABNT** aplica.
 *
 * É o mesmo que o documento novo já recebe (`criarDocumentoPadrao`): número de
 * página, abertura de capítulo e estilo das faixas na medida da norma, com a letra
 * capitulada desligada. Existe como função porque o caminho de volta precisa ser
 * explícito: quem mexeu no estilo e se perdeu tem onde voltar — e o que volta é
 * exatamente o que um documento novo traz, e não uma segunda opinião.
 */
export function formatacaoPadrao(modelo) {
    return {
        folha: { ...folhaPadrao(modelo), estiloDaFaixa: estiloDaFaixaPadrao() },
        capitulo: formatoDoCapituloPadrao(modelo),
        capitular: false,
        paragrafo: 'RECUADO',
    };
}
/* ------------------------------------------------------------------ */
/* Abertura de capítulo                                                */
/* ------------------------------------------------------------------ */
/** Como o capítulo é anunciado antes do título. */
export const ROTULOS_DE_CAPITULO = ['PADRAO', 'ARABICO', 'ROMANO', 'NENHUM'];
export const ROTULO_ROTULO_DE_CAPITULO = {
    PADRAO: 'Como o modelo manda',
    ARABICO: 'Só o número (1, 2, 3)',
    ROMANO: 'Número romano (I, II, III)',
    NENHUM: 'Nada: só o título',
};
export const DESCRICAO_ROTULO_DE_CAPITULO = {
    PADRAO: 'Livro escreve o número (1., 2.) e numera as partes (1.1., 1.1.1.); trabalho e artigo usam a numeração da norma (1, 1.1).',
    ARABICO: 'O número sozinho, sem a palavra "capítulo".',
    ROMANO: 'Algarismos romanos, como nos livros clássicos e nas partes de uma obra.',
    NENHUM: 'O título abre a folha sem numeração nenhuma.',
};
export const ALINHAMENTOS_DE_CAPITULO = ['CENTRO', 'ESQUERDA'];
export const ROTULO_ALINHAMENTO_DE_CAPITULO = {
    CENTRO: 'Centralizado, em duas linhas',
    ESQUERDA: 'À esquerda, na mesma linha',
};
export const DESCRICAO_ALINHAMENTO_DE_CAPITULO = {
    CENTRO: 'O rótulo numa linha e o título na de baixo, como abre um capítulo de livro.',
    ESQUERDA: 'Rótulo e título na mesma linha ("1 INTRODUÇÃO"), como o trabalho acadêmico faz.',
};
/** O capítulo como ele nasce em cada modelo. */
export function formatoDoCapituloPadrao(modelo) {
    return modelo === 'LIVRO'
        ? {
            rotulo: 'PADRAO',
            alinhamento: 'CENTRO',
            /*
             * O título do capítulo sai em caixa alta em **todos** os modelos: é a regra da
             * norma no trabalho e no artigo, e a regra do próprio modelo no livro. Aqui o
             * campo nasce ligado porque é o que acontece na folha — quem lê o documento
             * gravado não pode achar que o livro abre o capítulo em caixa baixa.
             */
            caixaAlta: true,
            aumentoPt: 3,
            respiroAntesMm: 12,
            tituloIsolado: false,
        }
        : {
            rotulo: 'PADRAO',
            alinhamento: 'ESQUERDA',
            caixaAlta: true,
            aumentoPt: 0,
            /*
             * Respiro **zero** no trabalho acadêmico: a norma quer o título da seção
             * primária na parte superior da mancha. O respiro é para quem quer um título
             * solto na folha (livro), e não para a monografia — que já começa em folha
             * nova, com o título no alto.
             */
            respiroAntesMm: 0,
            tituloIsolado: false,
        };
}
/** O formato do capítulo do documento, com o padrão do modelo onde falta. */
export function formatoDoCapitulo(documento) {
    return { ...formatoDoCapituloPadrao(documento.modelo), ...(documento.capitulo ?? {}) };
}
const ROMANOS = [
    [1000, 'M'],
    [900, 'CM'],
    [500, 'D'],
    [400, 'CD'],
    [100, 'C'],
    [90, 'XC'],
    [50, 'L'],
    [40, 'XL'],
    [10, 'X'],
    [9, 'IX'],
    [5, 'V'],
    [4, 'IV'],
    [1, 'I'],
];
/** 1 vira I, 4 vira IV, 49 vira XLIX. Acima de 3999 devolve o próprio número. */
export function numeroEmRomano(numero) {
    if (!Number.isFinite(numero) || numero < 1)
        return '';
    if (numero > 3999)
        return String(Math.floor(numero));
    let resto = Math.floor(numero);
    let saida = '';
    for (const [valor, letra] of ROMANOS) {
        while (resto >= valor) {
            saida += letra;
            resto -= valor;
        }
    }
    return saida;
}
/**
 * O rótulo que abre o capítulo: "1.", "2", "II" ou nada.
 *
 * `padrao` é o que o modelo manda (`numerarSecoes` já sabe disso) e é o que sai
 * quando a pessoa não mexeu no formato — no livro, o número do capítulo com o ponto
 * ("1."), **sem** a palavra "capítulo"; é assim que um documento salvo antes deste
 * recurso continua saindo igual (Regra 04).
 */
export function rotuloDoCapitulo(opcoes) {
    switch (opcoes.formato.rotulo) {
        case 'NENHUM':
            return '';
        case 'ARABICO':
            return String(opcoes.ordinal);
        case 'ROMANO':
            return numeroEmRomano(opcoes.ordinal);
        case 'PADRAO':
        default:
            return opcoes.padrao;
    }
}
export function destaqueDaSecao(nivel) {
    if (nivel <= 1)
        return { caixaAlta: true, negrito: true, italico: false };
    if (nivel === 2)
        return { caixaAlta: false, negrito: true, italico: false };
    return { caixaAlta: false, negrito: false, italico: true };
}
/* ------------------------------------------------------------------ */
/* Páginas de anotação                                                 */
/* ------------------------------------------------------------------ */
/** Como a folha de anotação é pautada. */
export const FORMATOS_DE_ANOTACAO = ['LINHAS', 'PONTOS', 'BRANCA'];
export const ROTULO_FORMATO_DE_ANOTACAO = {
    LINHAS: 'Linhas (pauta de caderno)',
    PONTOS: 'Pontos (grade de bolinhas)',
    BRANCA: 'Folha em branco',
};
export const DESCRICAO_FORMATO_DE_ANOTACAO = {
    LINHAS: 'Pauta larga, de 8 mm, para escrever à mão com conforto.',
    PONTOS: 'Grade discreta de bolinhas, para escrever ou desenhar.',
    BRANCA: 'Nada impresso: a folha lisa, para colar, desenhar ou imprimir depois.',
};
/** Quantas folhas de anotação cabem no fim do documento. */
export const MAXIMO_FOLHAS_DE_ANOTACAO = 20;
/** As folhas de anotação pedidas, dentro do limite. */
export function folhasDeAnotacao(documento) {
    const pedidas = Math.floor(documento.folhasDeAnotacao ?? 1);
    if (!Number.isFinite(pedidas) || pedidas < 0)
        return 0;
    return Math.min(pedidas, MAXIMO_FOLHAS_DE_ANOTACAO);
}
/**
 * Onde as folhas de anotação entram no documento.
 *
 * `ENTRE_CAPITULOS`: o mesmo jogo de folhas entra **antes de cada capítulo** — quem
 * escreve à mão anota ao lado do capítulo que está lendo, e não tudo no fim do
 * trabalho, onde a anotação já não serve para nada. O primeiro capítulo não ganha
 * folhas: não há nada antes dele para anotar.
 *
 * `FIM`: todas as folhas juntas no fim do documento (era o único lugar antes disto).
 */
export const POSICOES_DA_ANOTACAO = ['ENTRE_CAPITULOS', 'FIM'];
export const ROTULO_POSICAO_DA_ANOTACAO = {
    ENTRE_CAPITULOS: 'Antes de cada capítulo',
    FIM: 'No fim do documento',
};
export const DESCRICAO_POSICAO_DA_ANOTACAO = {
    ENTRE_CAPITULOS: 'O jogo de folhas se repete antes de cada capítulo (o primeiro não conta: não há nada antes dele). Um trabalho com três capítulos e duas folhas sai com quatro folhas de anotação — as duas que antecedem o 2º e o 3º capítulos.',
    FIM: 'Todas as folhas saem juntas, no fim do documento.',
};
/**
 * Onde as folhas de anotação entram.
 *
 * **Ausente = entre os capítulos**, que é o que se espera de um trabalho dividido em
 * capítulos: a folha de anotação serve para anotar **enquanto se lê**, e no fim do
 * documento ela chega tarde. Quem preferir todas juntas escolhe *No fim do documento*
 * no painel — a escolha fica gravada no documento, então não muda sozinha depois.
 */
export function posicaoDaAnotacao(documento) {
    return documento.anotacaoPosicao ?? 'ENTRE_CAPITULOS';
}
const vazia = () => ({ tipo: 'VAZIA' });
const comTexto = (texto) => (texto.trim() ? { tipo: 'TEXTO', texto: texto.trim() } : vazia());
const comNumero = () => ({ tipo: 'NUMERO' });
/** As seis caixas vazias, para montar por cima. */
function caixasVazias() {
    return {
        alto: { esquerda: vazia(), centro: vazia(), direita: vazia() },
        pe: { esquerda: vazia(), centro: vazia(), direita: vazia() },
    };
}
/** A posição pedida, traduzida para a caixa da faixa. */
function caixaDe(posicao) {
    return { ESQUERDA: 'esquerda', CENTRO: 'centro', DIREITA: 'direita' }[posicao];
}
/** A ponta oposta (o centro é vizinho de si mesmo). */
function oposta(caixa) {
    if (caixa === 'esquerda')
        return 'direita';
    if (caixa === 'direita')
        return 'esquerda';
    return 'centro';
}
/**
 * Em que caixa o texto cai: a pedida, a da outra ponta, ou o centro.
 *
 * A ordem importa. Quem pediu o texto à direita prefere a esquerda (e não o
 * centro) quando a direita está tomada — continua numa ponta, que é como as
 * faixas de livro são escritas.
 */
function caixaDoTexto(pedida, ocupadas) {
    for (const caixa of [pedida, oposta(pedida), 'centro']) {
        if (!ocupadas.includes(caixa))
            return caixa;
    }
    return pedida;
}
/**
 * As caixas que o número ocupa nesta faixa.
 *
 * `PE_EXTERNO` ocupa **as duas pontas** (uma em cada paridade), então com ele o
 * texto livre só encontra lugar no centro — e é isso que o editor explica.
 */
function caixasDoNumero(numeracao, onde) {
    if (numeracao === 'NENHUMA')
        return [];
    if (numeracao === 'PE_EXTERNO')
        return onde === 'pe' ? ['esquerda', 'direita'] : [];
    const [faixa, caixa] = {
        TOPO_DIREITA: ['alto', 'direita'],
        TOPO_CENTRO: ['alto', 'centro'],
        PE_CENTRO: ['pe', 'centro'],
        PE_DIREITA: ['pe', 'direita'],
    }[numeracao];
    return faixa === onde ? [caixa] : [];
}
/** O título do documento como ele aparece na faixa: título e subtítulo. */
export function tituloDaFaixa(documento) {
    return [documento.identificacao.titulo, documento.identificacao.subtitulo]
        .map((parte) => (parte ?? '').trim())
        .filter(Boolean)
        .join(': ');
}
/** Os autores como eles aparecem na faixa: "Ana Souza; João Lima". */
export function autoresDaFaixa(documento) {
    return documento.identificacao.autores
        .map((autor) => autor.nome.trim())
        .filter(Boolean)
        .join('; ');
}
/**
 * Monta a folha do documento.
 *
 * O número tem a posição pedida; o cabeçalho e o rodapé entram nas caixas que o
 * estilo escolhido pedir. `PE_EXTERNO` é o único caso em que a folha par sai
 * diferente da ímpar — e aí as duas versões são devolvidas inteiras.
 */
export function molduraDaFolha(documento) {
    const folha = folhaDoDocumento(documento);
    const titulo = comTexto(tituloDaFaixa(documento));
    const autores = comTexto(autoresDaFaixa(documento));
    const comum = caixasVazias();
    /*
     * O **número tem preferência**: ele é a norma e o texto é acabamento. A caixa
     * que o número ocupa entra na lista de ocupadas antes de o texto ser colocado,
     * e o texto procura a caixa pedida, depois a da outra ponta, depois o centro.
     *
     * Sem esta ordem, um rodapé livre à direita e o número no pé de fora (que
     * alterna de lado) sairiam impressos um em cima do outro — e ninguém confere o
     * pé da folha procurando isso.
     */
    const ocupadas = (onde) => caixasDoNumero(folha.numeracao, onde);
    const porTexto = (texto, pedida, onde) => {
        const livre = caixaDoTexto(caixaDe(pedida ?? 'CENTRO'), ocupadas(onde));
        comum[onde][livre] = comTexto(texto);
    };
    switch (folha.cabecalho) {
        case 'TITULO': {
            const livre = caixaDoTexto('centro', ocupadas('alto'));
            comum.alto[livre] = titulo;
            break;
        }
        case 'AUTOR_E_TITULO': {
            /*
             * A dupla espelhada: quem escreveu de um lado, o título do outro. Se o
             * número ocupar uma das pontas, o texto que ia para lá desce para o centro
             * — as duas pontas ficam com quem tem mais direito a elas.
             */
            const ocupadasNoAlto = ocupadas('alto');
            const caixaDoAutor = caixaDoTexto('esquerda', ocupadasNoAlto);
            comum.alto[caixaDoAutor] = autores;
            comum.alto[caixaDoTexto('direita', [...ocupadasNoAlto, caixaDoAutor])] = titulo;
            break;
        }
        case 'LIVRE':
            porTexto(folha.textoDoCabecalho ?? '', folha.posicaoDoCabecalho, 'alto');
            break;
        case 'NENHUM':
        default:
            break;
    }
    switch (folha.rodape) {
        case 'TITULO': {
            const livre = caixaDoTexto('centro', ocupadas('pe'));
            comum.pe[livre] = titulo;
            break;
        }
        case 'LIVRE':
            porTexto(folha.textoDoRodape ?? '', folha.posicaoDoRodape, 'pe');
            break;
        case 'NENHUM':
        default:
            break;
    }
    const moldura = {
        comum,
        filete: folha.filete,
        corpoPt: folha.corpoPt,
        estilo: estiloDaFaixa({ folha }),
        capituloEmFolhaNova: folha.capituloEmFolhaNova,
    };
    if (folha.numeracao === 'NENHUMA')
        return moldura;
    if (folha.numeracao === 'PE_EXTERNO') {
        /*
         * O número anda com a encadernação: à direita na folha ímpar, à esquerda na
         * par. São duas molduras inteiras (e não um remendo numa caixa) porque o CSS
         * de página par substitui a regra toda.
         */
        moldura.impares = clonar(comum);
        moldura.impares.pe.direita = comNumero();
        moldura.pares = clonar(comum);
        moldura.pares.pe.esquerda = comNumero();
        return moldura;
    }
    const [onde] = { TOPO_DIREITA: ['alto'], TOPO_CENTRO: ['alto'], PE_CENTRO: ['pe'], PE_DIREITA: ['pe'] }[folha.numeracao];
    comum[onde][caixasDoNumero(folha.numeracao, onde)[0]] = comNumero();
    return moldura;
}
function clonar(faixas) {
    return {
        alto: { ...faixas.alto },
        pe: { ...faixas.pe },
    };
}
/* ------------------------------------------------------------------ */
/* Folhas de abertura                                                  */
/* ------------------------------------------------------------------ */
/**
 * As peças que saem **sem faixa nenhuma** — nem cabeçalho, nem número.
 *
 * É a regra do livro impresso e da norma ao mesmo tempo: a capa, a folha de
 * rosto, o resumo e o sumário não levam número (na ABNT a contagem corre desde a
 * capa, mas o número só aparece a partir do texto). Sem esta lista, a capa sairia
 * com um "1" no canto — e ninguém revisa a capa procurando isso.
 */
export const ELEMENTOS_SEM_FAIXA = [
    'CAPA',
    'FOLHA_DE_ROSTO',
    'FOLHA_DE_APROVACAO',
    'DEDICATORIA',
    'AGRADECIMENTOS',
    'EPIGRAFE',
    'RESUMO',
    'ABSTRACT',
    'LISTA_DE_FIGURAS',
    'LISTA_DE_TABELAS',
    'LISTA_DE_ABREVIATURAS',
    'SUMARIO',
    'RESUMO_DO_ARTIGO',
    'APRESENTACAO',
];
/** Esta peça sai sem cabeçalho e sem número? */
export function semFaixa(elemento) {
    return ELEMENTOS_SEM_FAIXA.includes(elemento);
}
/** O nome da página nomeada das folhas de abertura (o CSS usa para limpá-las). */
export const PAGINA_DE_ABERTURA = 'abertura';
/**
 * O nome da página da folha de anotação.
 *
 * É uma página **própria**, e não só uma folha sem faixa: a folha de anotação usa
 * a folha inteira, com a margem estreita da anotação
 * (`MARGEM_DA_ANOTACAO_MM`), porque quem escreve à mão precisa do papel — as
 * margens da norma cortariam a área da escrita. Como toda página nomeada, ela
 * obriga uma quebra antes, e a folha de anotação já começa em folha nova.
 */
export const PAGINA_DA_ANOTACAO = 'anotacao';
/**
 * A página nomeada de cada peça, para o CSS.
 *
 * Só as folhas de abertura têm nome: elas trocam a moldura inteira (sem faixa,
 * sem número). O resto do documento usa a folha comum. O nome de página do CSS
 * obriga uma quebra antes de cada peça nomeada — e todas essas peças já começam
 * em folha nova, então não muda nada no papel.
 */
export function nomeDaPagina(elemento) {
    return semFaixa(elemento) ? PAGINA_DE_ABERTURA : undefined;
}
//# sourceMappingURL=folha.js.map